import { AssetClass } from "@hydrologiq/gql-experience/graphql"
import type { DeviceSource, MappedAsset } from "../../hooks/useDataConnectionMapping"
import type { SystemAsset } from "../../providers/MapAssetDataProvider"
import { formatAssetClass } from "@hydrologiq/shared/asset/PaginatedTable"

export { parseSourceLabel, type ParsedSourceLabel } from "@hydrologiq/shared/data-connections/MappingDisplay"

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface ChipColors {
  background: string
  color: string
}

// ─── formatAssetType ──────────────────────────────────────────────────────────

/** Format asset class enum/string to user-friendly display label */
export const formatAssetType = (assetClass: AssetClass | string | null | undefined): string => {
  return formatAssetClass(assetClass)
}

// ─── getAssetTypeChipColors ───────────────────────────────────────────────────

const ASSET_TYPE_COLORS: Record<string, ChipColors> = {
  Generator: { background: "rgba(100,116,139,0.16)", color: "#475569" },
  Solar: { background: "rgba(16,185,129,0.16)", color: "#047857" },
  Battery: { background: "rgba(245,158,11,0.18)", color: "#B45309" },
  "Inverter/Charger": { background: "rgba(37,99,235,0.14)", color: "#1D4ED8" },
  "Fuel storage": { background: "rgba(139,92,246,0.14)", color: "#7C3AED" },
  Distro: { background: "rgba(37,99,235,0.14)", color: "#2563EB" },
  Storage: { background: "#EEEEEE", color: "#545454" },
  Power: { background: "#EEEEEE", color: "#545454" },
  Monitoring: { background: "#EEEEEE", color: "#545454" },
  Gas: { background: "#EEEEEE", color: "#545454" },
  Refueller: { background: "#EEEEEE", color: "#545454" },
}

const DEFAULT_CHIP_COLORS: ChipColors = { background: "#EEEEEE", color: "#545454" }

/**
 * Returns chip background and text colors for a given asset type display label.
 * Uses the display label (output of formatAssetType) as the key.
 */
export function getAssetTypeChipColors(assetType: string): ChipColors {
  return ASSET_TYPE_COLORS[assetType] ?? DEFAULT_CHIP_COLORS
}

// ─── computeAvailableAssets ───────────────────────────────────────────────────

/**
 * Returns system assets that are not already mapped in the current connection,
 * optionally excluding one asset ID (for the current row being edited).
 */
export function computeAvailableAssets(
  systemAssets: SystemAsset[],
  mappedAssets: MappedAsset[],
  excludeAssetId?: string,
): SystemAsset[] {
  const mappedAssetIds = new Set(
    mappedAssets
      .filter((m) => m.assetId !== excludeAssetId)
      .map((m) => m.assetId),
  )
  return systemAssets.filter((a) => !mappedAssetIds.has(a.id))
}

// ─── computeAvailableSources ──────────────────────────────────────────────────

/**
 * Returns device sources that are not already mapped in the current connection,
 * optionally excluding one source ref (for the current row being edited).
 * Empty sourceRefs (unassigned mappings) are ignored — they don't claim any source.
 */
export function computeAvailableSources(
  sources: DeviceSource[],
  mappedAssets: MappedAsset[],
  excludeSourceRef?: string,
): DeviceSource[] {
  const mappedRefs = new Set(
    mappedAssets
      .filter((m) => m.sourceRef && m.sourceRef !== excludeSourceRef)
      .map((m) => m.sourceRef),
  )
  return sources.filter((s) => !mappedRefs.has(s.ref))
}

// ─── isSourceStale ────────────────────────────────────────────────────────────

/**
 * Returns true if the given sourceRef is NOT present in the available sources array,
 * indicating the mapping is stale (pointing to a source that no longer exists).
 *
 * An empty sourceRef means the asset has no source assigned yet (e.g. from legacy
 * auto-mapping) — this is treated as "needs attention" but is NOT stale in the
 * traditional sense (the source wasn't removed, it was never set).
 */
export function isSourceStale(sourceRef: string, availableSources: DeviceSource[]): boolean {
  if (!sourceRef) return false
  return !availableSources.some((s) => s.ref === sourceRef)
}

/**
 * Returns true if the mapping needs source attention — either the source is
 * stale (pointing to a removed source) or unassigned (empty sourceRef).
 */
export function isSourceMissing(sourceRef: string, availableSources: DeviceSource[]): boolean {
  if (!sourceRef) return true
  return !availableSources.some((s) => s.ref === sourceRef)
}

// ─── formatStaleBanner ────────────────────────────────────────────────────────

/**
 * Returns a singular or plural warning message based on the number of stale mappings.
 */
export function formatStaleBanner(staleCount: number): string {
  if (staleCount === 1) {
    return "One mapped asset points to a source that is no longer available from this connection. It will not pull in data until it is remapped to a current source."
  }
  return `${staleCount} mapped assets point to sources that are no longer available from this connection. They will not pull in data until they are remapped to current sources.`
}
